/**
 * PreviewLightbox — portal-based zoomable image viewer.
 *
 * Purpose: give the Teacher Editor + Student Lesson View a fast, uniform
 * way to open any preview image (chart PNG, drawing PNG, AI-generated
 * image) in a fullscreen modal with wheel-zoom and drag-pan.
 *
 * Design notes
 *  • Renders into document.body via a portal so overflow:hidden ancestors
 *    don't clip it.
 *  • No third-party deps — plain React state + transforms. Keeps the
 *    editor bundle lean (matters when a topic has 10+ heavy previews).
 *  • The image inside uses decoding="async" + fetchpriority="high" so
 *    the full-res decode only happens when the lightbox actually opens.
 *  • Esc key + backdrop click close. Wheel = zoom. Drag = pan.
 */
import React, { useEffect, useRef, useState, useCallback } from 'react';
import { createPortal } from 'react-dom';
import { X, ZoomIn, ZoomOut, Maximize2, Download } from 'lucide-react';

const MIN_SCALE = 0.5;
const MAX_SCALE = 6;

export default function PreviewLightbox({ src, alt = '', caption = '', onClose }) {
  const [scale, setScale] = useState(1);
  const [pos, setPos] = useState({ x: 0, y: 0 });
  const dragging = useRef(null); // {startX, startY, ox, oy}
  const imgRef = useRef(null);

  // Close on Esc + lock body scroll
  useEffect(() => {
    const onKey = (e) => { if (e.key === 'Escape') onClose?.(); };
    document.addEventListener('keydown', onKey);
    const prevOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      document.removeEventListener('keydown', onKey);
      document.body.style.overflow = prevOverflow;
    };
  }, [onClose]);

  const reset = useCallback(() => { setScale(1); setPos({ x: 0, y: 0 }); }, []);

  const onWheel = useCallback((e) => {
    e.preventDefault();
    const delta = e.deltaY < 0 ? 0.15 : -0.15;
    setScale((s) => {
      const next = Math.min(MAX_SCALE, Math.max(MIN_SCALE, s + delta * s));
      if (next <= 1.001) { setPos({ x: 0, y: 0 }); return 1; }
      return next;
    });
  }, []);

  const onMouseDown = (e) => {
    if (scale <= 1.001) return;
    dragging.current = { startX: e.clientX, startY: e.clientY, ox: pos.x, oy: pos.y };
  };
  const onMouseMove = (e) => {
    if (!dragging.current) return;
    setPos({
      x: dragging.current.ox + (e.clientX - dragging.current.startX),
      y: dragging.current.oy + (e.clientY - dragging.current.startY),
    });
  };
  const onMouseUp = () => { dragging.current = null; };

  const zoomIn = () => setScale((s) => Math.min(MAX_SCALE, s * 1.4));
  const zoomOut = () => setScale((s) => {
    const next = Math.max(MIN_SCALE, s / 1.4);
    if (next <= 1.001) { setPos({ x: 0, y: 0 }); return 1; }
    return next;
  });

  const download = () => {
    try {
      const a = document.createElement('a');
      a.href = src;
      a.download = (alt || 'preview') + (src.startsWith('data:image/png') ? '.png' : '.img');
      document.body.appendChild(a);
      a.click();
      a.remove();
    } catch (_) { /* no-op */ }
  };

  const cursor = scale > 1.001 ? (dragging.current ? 'grabbing' : 'grab') : 'zoom-in';

  const node = (
    <div
      className="fixed inset-0 z-[9999] flex items-center justify-center bg-black/85 backdrop-blur-sm animate-in fade-in duration-150"
      onClick={(e) => { if (e.target === e.currentTarget) onClose?.(); }}
      data-testid="preview-lightbox"
    >
      {/* Toolbar */}
      <div className="absolute top-4 right-4 z-10 flex items-center gap-1.5 bg-white/10 backdrop-blur-md rounded-full px-2 py-1.5 border border-white/15" style={{ pointerEvents: 'auto' }}>
        <button onClick={zoomOut} className="w-8 h-8 rounded-full hover:bg-white/15 text-white flex items-center justify-center transition-colors" title="Zoom out" data-testid="lightbox-zoom-out">
          <ZoomOut className="w-4 h-4" />
        </button>
        <span className="text-white text-[11px] font-bold tabular-nums w-12 text-center" data-testid="lightbox-scale">{Math.round(scale * 100)}%</span>
        <button onClick={zoomIn} className="w-8 h-8 rounded-full hover:bg-white/15 text-white flex items-center justify-center transition-colors" title="Zoom in" data-testid="lightbox-zoom-in">
          <ZoomIn className="w-4 h-4" />
        </button>
        <button onClick={reset} className="w-8 h-8 rounded-full hover:bg-white/15 text-white flex items-center justify-center transition-colors" title="Fit" data-testid="lightbox-reset">
          <Maximize2 className="w-4 h-4" />
        </button>
        <div className="w-px h-5 bg-white/20 mx-1" />
        <button onClick={download} className="w-8 h-8 rounded-full hover:bg-white/15 text-white flex items-center justify-center transition-colors" title="Download" data-testid="lightbox-download">
          <Download className="w-4 h-4" />
        </button>
        <button onClick={onClose} className="w-8 h-8 rounded-full hover:bg-white/15 text-white flex items-center justify-center transition-colors" title="Close (Esc)" data-testid="lightbox-close">
          <X className="w-4 h-4" />
        </button>
      </div>

      {/* Image */}
      <div
        className="w-full h-full flex items-center justify-center overflow-hidden select-none"
        onWheel={onWheel}
        onMouseDown={onMouseDown}
        onMouseMove={onMouseMove}
        onMouseUp={onMouseUp}
        onMouseLeave={onMouseUp}
        onClick={(e) => {
          // Inner wrapper covers 100% of the backdrop, so it's the div
          // that actually receives "backdrop" clicks — close when the
          // click was outside the <img> itself, else treat as zoom-in.
          if (e.target === e.currentTarget) {
            onClose?.();
            return;
          }
          if (scale <= 1.001 && e.target === imgRef.current) zoomIn();
        }}
        style={{ cursor }}
      >
        <img
          ref={imgRef}
          src={src}
          alt={alt}
          draggable={false}
          decoding="async"
          fetchPriority="high"
          className="max-w-[92vw] max-h-[86vh] object-contain will-change-transform transition-transform duration-100 ease-out"
          style={{ transform: `translate(${pos.x}px, ${pos.y}px) scale(${scale})` }}
          data-testid="lightbox-image"
        />
      </div>

      {/* Caption */}
      {caption && (
        <div className="absolute bottom-5 left-1/2 -translate-x-1/2 max-w-[80vw] text-center">
          <p className="text-white/85 text-[12px] italic px-4 py-1.5 rounded-full bg-white/8 backdrop-blur-md border border-white/10">{caption}</p>
        </div>
      )}

      {/* Hint */}
      <div className="absolute bottom-5 right-5 text-white/50 text-[10px] tracking-wide">
        Scroll to zoom · Drag to pan · Esc to close
      </div>
    </div>
  );

  return createPortal(node, document.body);
}
